import React from 'react'
import './css/termsModal.css'

function PrivacyPolicyModal({ onClose }) {
  return (
    <div className="terms-modal-overlay" onClick={onClose}>
      <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
        <div className="terms-modal-header">
          <h2>BuzzTickets Privacy Policy</h2>
          <button className="terms-modal-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="terms-modal-body">
          <p className="terms-effective-date">
            Effective Date: July 2, 2026 &nbsp;|&nbsp; Last Updated: July 2, 2026
          </p>

          <h3>Introduction</h3>
          <p>
            Welcome to BuzzTickets, an online event management and ticketing platform developed,
            owned, and operated by SmartBuilders (Pty) Ltd ("SmartBuilders", "Company", "we", "our",
            or "us"). This Privacy Policy explains how we collect, use, store, disclose, protect, and
            process your Personal Information when you use the BuzzTickets website, mobile
            application, and related services ("Service"). It also explains your privacy rights under
            applicable laws, including the Protection of Personal Information Act, 2013 ("POPIA").
          </p>
          <p>
            By creating an account, purchasing tickets, hosting events, accessing, or using the
            Service, you acknowledge that you have read and understood this Privacy Policy.
          </p>

          <h3>1. Interpretation and Definitions</h3>
          <p>For the purposes of this Privacy Policy:</p>
          <ul>
            <li>
              <strong>Application</strong> means the BuzzTickets website, mobile application, and
              associated software developed and operated by SmartBuilders (Pty) Ltd.
            </li>
            <li>
              <strong>Business Data</strong> means information submitted by Event Organizers relating
              to events hosted on the platform, including event details, ticket information, attendee
              information, sales data, reports, analytics, promotional material, and related
              operational information.
            </li>
            <li>
              <strong>Company, SmartBuilders, we, our, or us</strong> means SmartBuilders (Pty) Ltd.
            </li>
            <li>
              <strong>Event Organizer</strong> means any individual, business, organization, or legal
              entity that creates, promotes, manages, or hosts an event using BuzzTickets.
            </li>
            <li>
              <strong>Personal Information</strong> means information relating to an identified or
              identifiable natural or juristic person as defined by POPIA. For users outside South
              Africa, this includes Personal Data under applicable privacy laws.
            </li>
            <li>
              <strong>Service</strong> means the BuzzTickets platform, including its website, mobile
              application, support services, and associated software.
            </li>
            <li>
              <strong>Usage Data</strong> means technical and diagnostic information automatically
              generated through your use of the Service, including device information, application
              performance, browser information, crash reports, and analytics.
            </li>
            <li>
              <strong>You</strong> means the individual purchasing tickets, creating events, browsing
              the platform, or otherwise using the Service.
            </li>
          </ul>

          <h3>2. About BuzzTickets</h3>
          <p>
            BuzzTickets is an online event management and ticketing platform that enables Event
            Organizers to create, promote, manage, and sell tickets for events while providing
            attendees with a secure and convenient ticket purchasing experience.
          </p>
          <p>
            BuzzTickets is a software platform developed, owned, and operated by SmartBuilders (Pty)
            Ltd. Neither BuzzTickets nor SmartBuilders (Pty) Ltd is the organizer, promoter, venue
            owner, sponsor, or operator of events listed on the platform unless expressly stated
            otherwise. Event Organizers are independent parties and are solely responsible for
            managing their own events and complying with applicable laws, including privacy
            legislation.
          </p>

          <h3>3. Information We Collect</h3>
          <p>
            Depending on how you use BuzzTickets, SmartBuilders (Pty) Ltd may collect the following
            information.
          </p>
          <p><strong>Personal Information</strong></p>
          <p>We may collect:</p>
          <ul>
            <li>Full name</li>
            <li>Email address</li>
            <li>Mobile phone number</li>
            <li>Password (stored securely in encrypted form)</li>
            <li>Profile information</li>
            <li>Business or organization name (for Event Organizers)</li>
          </ul>
          <p><strong>Event Information</strong></p>
          <p>If you are an Event Organizer, we may collect:</p>
          <ul>
            <li>Event names</li>
            <li>Event descriptions</li>
            <li>Event dates and times</li>
            <li>Venue information</li>
            <li>Ticket pricing</li>
            <li>Ticket availability</li>
            <li>Images and promotional material</li>
            <li>Attendance information</li>
            <li>Sales statistics</li>
            <li>Organizer account information</li>
          </ul>
          <p><strong>Ticket Purchase Information</strong></p>
          <p>When purchasing tickets, we may collect:</p>
          <ul>
            <li>Purchaser name</li>
            <li>Email address</li>
            <li>Phone number</li>
            <li>Ticket type</li>
            <li>Ticket quantity</li>
            <li>Booking reference numbers</li>
            <li>Purchase history</li>
          </ul>
          <p><strong>Payment Information</strong></p>
          <p>Payments are securely processed by PayStack.</p>
          <p>
            Neither BuzzTickets nor SmartBuilders (Pty) Ltd stores your debit or credit card
            information. Payment providers may collect and process payment information in accordance
            with their own privacy policies and terms of service.
          </p>
          <p><strong>Usage Data</strong></p>
          <p>When using the Service, we may automatically collect technical information including:</p>
          <ul>
            <li>Device type</li>
            <li>Device identifiers</li>
            <li>Browser type</li>
            <li>Operating system</li>
            <li>IP address</li>
            <li>Application version</li>
            <li>Log information</li>
            <li>Crash reports</li>
            <li>Performance metrics</li>
          </ul>

          <h3>4. Legal Basis for Processing</h3>
          <p>SmartBuilders (Pty) Ltd processes Personal Information only where permitted by applicable law.</p>
          <p>Processing is carried out where necessary to:</p>
          <ul>
            <li>provide the Service;</li>
            <li>perform our contractual obligations;</li>
            <li>process ticket purchases;</li>
            <li>administer events;</li>
            <li>comply with legal obligations;</li>
            <li>protect our legitimate business interests;</li>
            <li>prevent fraud and abuse;</li>
            <li>improve our products and services; or</li>
            <li>where you have provided consent.</li>
          </ul>

          <h3>5. How We Use Your Information</h3>
          <p>SmartBuilders (Pty) Ltd may use your Personal Information and Business Data to:</p>
          <ul>
            <li>create and manage your account;</li>
            <li>process ticket purchases;</li>
            <li>generate and deliver digital tickets;</li>
            <li>verify ticket ownership;</li>
            <li>enable Event Organizers to manage their events;</li>
            <li>process organizer settlements;</li>
            <li>provide customer support;</li>
            <li>send booking confirmations and receipts;</li>
            <li>send important account and event notifications;</li>
            <li>improve the functionality, security, and performance of BuzzTickets;</li>
            <li>detect fraud, abuse, and suspicious activity;</li>
            <li>comply with legal and regulatory obligations;</li>
            <li>generate reports, analytics, and business insights.</li>
          </ul>

          <h3>6. Information Shared with Event Organizers</h3>
          <p>
            When you purchase a ticket, certain information is shared with the relevant Event
            Organizer, including:
          </p>
          <ul>
            <li>Your name</li>
            <li>Email address</li>
            <li>Phone number (where applicable)</li>
            <li>Ticket type</li>
            <li>Ticket quantity</li>
            <li>Booking reference</li>
            <li>Attendance status</li>
          </ul>
          <p>This information enables Event Organizers to:</p>
          <ul>
            <li>verify ticket validity;</li>
            <li>manage event admissions;</li>
            <li>contact attendees regarding event updates;</li>
            <li>respond to customer enquiries; and</li>
            <li>administer their events effectively.</li>
          </ul>
          <p>
            Once this information has been shared for legitimate event administration purposes,
            BuzzTickets and SmartBuilders (Pty) Ltd are not responsible for how Event Organizers
            process or use your Personal Information.
          </p>
          <p>
            Event Organizers are independent data custodians and are responsible for complying with
            applicable privacy legislation, including POPIA.
          </p>

          <h3>7. Information We Share</h3>
          <p>SmartBuilders (Pty) Ltd does not sell, rent, or trade your Personal Information.</p>
          <p>Information may be shared only where necessary with:</p>
          <ul>
            <li>Event Organizers;</li>
            <li>payment service providers;</li>
            <li>cloud hosting providers;</li>
            <li>email and notification service providers;</li>
            <li>fraud prevention and security providers;</li>
            <li>professional advisers, auditors, and insurers;</li>
            <li>
              government authorities, regulators, or law enforcement agencies where required by law.
            </li>
          </ul>
          <p>
            Information is shared only for legitimate operational, contractual, legal, or security
            purposes.
          </p>

          <h3>8. Marketing Communications</h3>
          <p>With your consent, SmartBuilders (Pty) Ltd may send:</p>
          <ul>
            <li>platform updates;</li>
            <li>feature announcements;</li>
            <li>promotional offers;</li>
            <li>product news;</li>
            <li>service notifications; and</li>
            <li>company news relating to BuzzTickets.</li>
          </ul>
          <p>
            You may unsubscribe from marketing communications at any time using the unsubscribe option
            provided.
          </p>
          <p>
            Essential transactional emails, booking confirmations, account notifications, and security
            notices cannot be disabled while your account remains active.
          </p>

          <h3>9. Data Storage and Security</h3>
          <p>
            SmartBuilders (Pty) Ltd implements commercially reasonable administrative, technical, and physical
            safeguards to protect your Personal Information.
          </p>
          <p>These safeguards include:</p>
          <ul>
            <li>HTTPS encryption;</li>
            <li>encryption in transit;</li>
            <li>encrypted password storage;</li>
            <li>password hashing;</li>
            <li>secure authentication controls;</li>
            <li>role-based access controls;</li>
            <li>restricted employee access;</li>
            <li>secure cloud infrastructure;</li>
            <li>firewalls and network security controls;</li>
            <li>routine system monitoring and security practices.</li>
          </ul>
          <p>
            Although we take reasonable steps to protect your information, no internet-based service or
            electronic storage method can be guaranteed to be completely secure.
          </p>

          <h3>10. Data Retention</h3>
          <p>SmartBuilders (Pty) Ltd retains Personal Information only for as long as reasonably necessary.</p>
          <p><strong>Account Information</strong></p>
          <p>
            Retained for the duration of your account and up to twelve (12) months after account
            closure. Data is kept confidential and not divulged to any outside sources.
          </p>
          <p><strong>Ticket Purchase Records</strong></p>
          <p>
            Retained as required for accounting, fraud prevention, dispute resolution, legal
            compliance, and regulatory purposes.
          </p>
          <p><strong>Event Information</strong></p>
          <p>
            Retained while an event remains active and for a reasonable period thereafter unless
            deletion is requested or required by law.
          </p>
          <p><strong>Support Records</strong></p>
          <p>Retained for up to twelve (12) months after a support request has been resolved.</p>
          <p><strong>Usage Data</strong></p>
          <p>Retained for diagnostics, fraud prevention, security, analytics, and service improvements.</p>
          <p>Certain information may be retained for longer periods where required by law.</p>

          <h3>11. Your Rights</h3>
          <p>Subject to applicable law, including POPIA, you may request to:</p>
          <ul>
            <li>access your Personal Information;</li>
            <li>correct inaccurate information;</li>
            <li>update your account information;</li>
            <li>request deletion of your Personal Information where legally permissible;</li>
            <li>object to certain processing activities;</li>
            <li>withdraw consent where processing is based on consent; and</li>
            <li>request information regarding how your Personal Information is processed.</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us using the contact information provided
            below. We may request reasonable proof of identity before processing your request. Certain
            requests may be declined where SmartBuilders (Pty) Ltd is legally required to retain information.
          </p>

          <h3>12. Account Deletion</h3>
          <p>You may request deletion of your BuzzTickets account at any time.</p>
          <p>
            Certain information may be retained where required for legal compliance, accounting, fraud
            prevention, dispute resolution, regulatory obligations, or enforcement of our legal
            rights.
          </p>

          <h3>13. Children's Privacy</h3>
          <p>
            BuzzTickets is intended for users aged 18 years or older or for users who have the consent
            of a parent or legal guardian.
          </p>
          <p>
            SmartBuilders (Pty) Ltd does not knowingly collect Personal Information from children without
            appropriate authorization.
          </p>

          <h3>14. International Processing</h3>
          <p>
            Your Personal Information may be processed or stored using secure cloud infrastructure
            located inside or outside South Africa.
          </p>
          <p>
            Where international transfers occur, SmartBuilders (Pty) Ltd will implement reasonable safeguards to
            protect Personal Information in accordance with applicable privacy legislation.
          </p>

          <h3>15. Third-Party Services</h3>
          <p>BuzzTickets integrates with trusted third-party providers including:</p>
          <ul>
            <li>PayStack;</li>
            <li>cloud hosting providers;</li>
            <li>email delivery services;</li>
            <li>analytics providers; and</li>
            <li>notification services.</li>
          </ul>
          <p>These providers operate under their own privacy policies and terms of service.</p>
          <p>
            BuzzTickets and SmartBuilders (Pty) Ltd are not responsible for the privacy practices of
            third-party providers. Users are encouraged to review the privacy policies of third-party
            providers before using their services.
          </p>

          <h3>16. POPIA Compliance</h3>
          <p>
            For the purposes of the Protection of Personal Information Act, 2013 (POPIA), SmartBuilders
            (Pty) Ltd is the Responsible Party for the Personal Information processed through
            BuzzTickets.
          </p>
          <p>
            We process Personal Information lawfully, reasonably, transparently, and only for
            legitimate business purposes. Where required, your consent will be obtained before
            processing Personal Information.
          </p>

          <h3>17. Data Breaches</h3>
          <p>
            Should SmartBuilders (Pty) Ltd become aware of a security breach affecting your Personal Information,
            we will investigate the incident and notify affected users and the relevant regulatory
            authorities where required by applicable law.
          </p>

          <h3>18. Changes to this Privacy Policy</h3>
          <p>SmartBuilders (Pty) Ltd may amend this Privacy Policy from time to time.</p>
          <p>
            Updated versions will be published within the BuzzTickets platform and on our website
            together with a revised Last Updated date.
          </p>
          <p>
            Your continued use of the Service after changes become effective constitutes acceptance of
            the revised Privacy Policy.
          </p>

          <h3>19. Contact Us</h3>
          <p>
            If you have any questions regarding this Privacy Policy or wish to exercise your privacy
            rights, please contact:
          </p>
          <p>
            SmartBuilders (Pty) Ltd
            <br />
            Email: <a href="mailto:info@smart-builders.co.za">info@smart-builders.co.za</a>
            <br />
            Website:{' '}
            <a href="https://www.smart-builders.co.za/" target="_blank" rel="noopener noreferrer">
              https://www.smart-builders.co.za/
            </a>
          </p>
          <p>
            We aim to respond to privacy-related enquiries within a reasonable period and in
            accordance with applicable legal requirements.
          </p>

          <h3>20. Your Consent</h3>
          <p>
            By creating a BuzzTickets account, purchasing tickets, hosting events, or otherwise using
            the Service, you acknowledge and agree that:
          </p>
          <ul>
            <li>You have read and understood this Privacy Policy.</li>
            <li>
              You consent to the collection, storage, use, and processing of your Personal Information
              by SmartBuilders (Pty) Ltd as described in this Privacy Policy where consent is required.
            </li>
            <li>
              You understand that information necessary for ticket administration will be shared with
              the relevant Event Organizer.
            </li>
            <li>
              You acknowledge that payment information is securely processed by PayStack and is not
              stored by BuzzTickets or SmartBuilders (Pty) Ltd.
            </li>
            <li>
              You understand that BuzzTickets is a technology platform owned and operated by
              SmartBuilders (Pty) Ltd and is not responsible for the operation, management,
              administration, or fulfilment of events hosted by independent Event Organizers.
            </li>
            <li>
              You acknowledge that SmartBuilders (Pty) Ltd may amend this Privacy Policy from time to time and
              that your continued use of the Service constitutes acceptance of any updated version.
            </li>
          </ul>
        </div>

        <div className="terms-modal-footer">
          <button className="terms-modal-accept" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicyModal
